import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { interval, Subscription } from 'rxjs';
import { WithdrawalService } from '../../_core/_services/withdrawal.service';
import { WithdrawalDto } from '../../_core/_model/withdrawal';
import { FormatService } from '@shared/_core/_services/format';

@Component({
  selector: 'app-withdrawal-history',
  standalone: false,
  templateUrl: './withdrawal-history.html',
  styleUrl: './withdrawal-history.css'
})
export class WithdrawalHistory implements OnInit, OnDestroy {
  loading = false;
  page = 1;
  withdrawals: WithdrawalDto[] = [];
  meta: any = null;
  highlightedRef = '';
  pollingSub?: Subscription;
  retryingId = '';
  filterWalletId: string | null = null;

  selectedWithdrawal: WithdrawalDto | null = null;
  
  showDetailsModal = false;

  url: any;

  constructor(
    private withdrawalService: WithdrawalService,
    private route: ActivatedRoute, 
    private router: Router,
    private format: FormatService,
  ) {
    this.url = this.router.url.slice(1);
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.highlightedRef = params['ref'] || '';
      this.filterWalletId = params['walletId'] || null;

      this.loadWithdrawals();
    });
    this.startPolling();
  }

  backToWallet(): void {
    if (!this.filterWalletId) return;
    this.router.navigate(['/wallets/manage', this.filterWalletId]);
  }

  ngOnDestroy(): void {
    this.pollingSub?.unsubscribe();
  }

  loadWithdrawals(page:number = 1, silent = false): void {
    this.page = page;
    if (!silent) this.loading = true;

    this.withdrawalService.getUserWithdrawals(this.page, this.filterWalletId).subscribe({
      next: (res) => {
        this.withdrawals = res.data || [];
        this.meta = res.meta || null;
      },
      error: () => {
        if (!silent) this.withdrawals = [];
      },
      complete: () => {
        if (!silent) this.loading = false;
      }
    });
  }

  clearFilter(): void {
    this.filterWalletId = null;
    this.loadWithdrawals();
  }

  startPolling(): void {
    this.pollingSub = interval(10000).subscribe(() => {
      const hasPending = this.withdrawals.some(x =>
        ['pending', 'processing'].includes((x?.status || '').toLowerCase())
      );

      if (hasPending) {
        this.loadWithdrawals(this.page, true);
      }
    });
  }

  badgeClass(status: string): string {
    switch ((status || '').toLowerCase()) {
      case 'completed':
      case 'confirmed':
        return 'bg-success-subtle text-success';
      case 'failed':
      case 'cancelled':
        return 'bg-danger-subtle text-danger';
      case 'processing':
      case 'pending':
        return 'bg-warning-subtle text-warning';
      default:
        return 'bg-secondary-subtle text-secondary';
    }
  }

  isHighlighted(item: WithdrawalDto): boolean {
    return !!this.highlightedRef && item.externalReference === this.highlightedRef;
  }
  
  canRetry(item: WithdrawalDto): boolean {
  return (item.status || '').toLowerCase() === 'failed'
    && !this.isRetryWithdrawal(item)
    && this.isWithinRetryWindow(item);
}

isRetryWithdrawal(item: WithdrawalDto): boolean {
  return (item.externalReference || '').toLowerCase().startsWith('retry_');
}

isWithinRetryWindow(item: WithdrawalDto): boolean {
  const failedAt = item.failedAt || item.lastUpdatedAt || item.createdAt;
  if (!failedAt) return false;

  const oneHourMs = 60 * 60 * 1000;
  return Date.now() - new Date(failedAt).getTime() <= oneHourMs;
}

  getDepositAndWithdrawalStatusClass(status: string, isWithdrawal: boolean): string {
    return this.format.getDepositAndWithdrawalStatusClass(status, isWithdrawal);
  }

  retry(item: WithdrawalDto): void {
  if (!item.withdrawalId || !this.canRetry(item)) return;

  this.retryingId = item.withdrawalId;

  this.withdrawalService.retryWithdrawal(item.withdrawalId).subscribe({
    next: () => {
      this.loadWithdrawals(this.page, true);
    },
    error: () => {
      this.retryingId = '';
    },
    complete: () => {
      this.retryingId = '';
    }
  });
}

  openDetails(item: WithdrawalDto): void {
    this.selectedWithdrawal = item;
    this.showDetailsModal = true;
  }

  closeDetails(): void {
    this.showDetailsModal = false;
    this.selectedWithdrawal = null;
  }
}